import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Dialog, DialogProps } from "../ui/Dialog"
import { Image } from "../ui/Image"
import { Button } from "../ui/Button"
import { analytics } from "@/telemetry/amplitude"
import toast from "react-hot-toast"
import clsx from "clsx"
import { isAcceptedFile, openFilesWithPicker } from "@/utils/file"
import { resizeImageToSize } from "@/core/utils/resize-image"
import {
	STYLE_COVER_LONGEST_SIDE,
	STYLE_COVER_OUTPUT_QUALITY,
} from "@/store/style/style-cover-constants"

const COVER_ACCEPTED_EXT_NAMES = [".jpeg", ".jpg"] satisfies `.${string}`[]

export const StyleUpdateCoverDialog = (
	props: DialogProps & {
		styleId: string
		onCompleteWithError: (result: { cover: Blob; isPublic: boolean }) => Promise<void>
		title: string
		actionTitle: string
		description?: string
		subTitle?: string
		errorMessage?: string
	},
) => {
	const { t } = useTranslation("components")
	const [newCover, setNewCover] = useState<Blob | null>(null)
	const [coverUrl, setCoverUrl] = useState<string | null>(null)
	const [isDragging, setIsDragging] = useState(false)

	useEffect(() => {
		const url = newCover ? URL.createObjectURL(newCover) : null
		setCoverUrl(url)

		return () => {
			if (url) {
				URL.revokeObjectURL(url)
			}
		}
	}, [newCover])

	const selectImages = async () => {
		const pickerOptions: OpenFilePickerOptions = {
			types: [
				{
					description: "Image", // i18n-ignore — browser File Picker API
					accept: {
						"image/jpeg": COVER_ACCEPTED_EXT_NAMES,
					},
				},
			],
			excludeAcceptAllOption: true,
			multiple: false,
		}

		try {
			const files = await openFilesWithPicker(pickerOptions)
			const cover = files[0]
			if (cover) {
				const scaledCover = await resizeImageToSize({
					image: cover,
					options: { outputQuality: STYLE_COVER_OUTPUT_QUALITY },
					longestSide: STYLE_COVER_LONGEST_SIDE,
				})
				setNewCover(scaledCover)
			}
		} catch {
			analytics.trackEvent({
				label: "select_images_error",
				type: "error",
				value: "file_picker",
			})
		}
	}

	const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
		setIsDragging(false)

		e.stopPropagation()
		e.preventDefault()

		const files = e.dataTransfer.files
		if (files.length > 1) {
			toast.error(t("styleUpdateCoverDialog.pleaseSelectUpTo1"))
			return
		}

		try {
			const cover = files[0]
			if (!cover || !isAcceptedFile(cover.name, COVER_ACCEPTED_EXT_NAMES)) {
				toast.error(t("styleUpdateCoverDialog.pleaseSelectAJpegFile"))
			} else {
				const scaledCover = await resizeImageToSize({
					image: cover,
					options: { outputQuality: STYLE_COVER_OUTPUT_QUALITY },
					longestSide: STYLE_COVER_LONGEST_SIDE,
				})
				setNewCover(scaledCover)
			}

			analytics.trackEvent({
				label: "select_images_success",
				type: "event",
				value: "drag_and_drop",
			})
		} catch (e: unknown) {
			console.error(e)
			analytics.trackEvent({
				label: "select_images_error",
				type: "error",
				value: "drag_and_drop",
			})
		}
	}

	const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		setIsDragging(true)
		const item = e.dataTransfer.items[0]
		if (!item) {
			return
		}
	}

	const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault()
		setIsDragging(false)
	}

	return (
		<Dialog
			contentSize="md"
			{...props}
			bottomButtons={
				<div className={"flex w-full flex-row items-center justify-end space-x-2"}>
					<Button
						trackingLabel="confirm"
						variant="primary"
						disabled={!newCover}
						onClick={() => {
							if (newCover) {
								void props.onCompleteWithError({
									cover: newCover,
									isPublic: false,
								})
							}
						}}
					>
						{props.actionTitle}
					</Button>
				</div>
			}
		>
			<div className="flex max-h-[80vh] w-full flex-col gap-4 overflow-hidden">
				<div className="flex w-full shrink-0 flex-col items-start space-y-2 pb-2">
					<div className="text-md font-bold">{props.title}</div>
					{props.description && (
						<div className="text-sm text-base-400">{props.description}</div>
					)}
				</div>
				<div
					className={clsx(
						"relative flex aspect-[16/9] h-full items-center justify-center overflow-hidden rounded-md border border-dashed border-base-400",
						isDragging ? "bg-base-400/30" : "bg-base-400/10",
					)}
					data-allow-file-drop
					onDragOver={handleDragOver}
					onDrop={(e) => void handleDrop(e)}
					onDragLeave={handleDragLeave}
				>
					{coverUrl && (
						<Image
							animateWhileLoading
							className="absolute inset-0 size-full object-cover"
							src={coverUrl}
							alt={`style_cover`}
						/>
					)}
					{!coverUrl && (
						<div className="flex w-full flex-col items-center justify-center space-y-2">
							<div className="text-xs">{t("styleUpdateCoverDialog.dropImage")}</div>
							<div className="flex flex-row items-center space-x-1 text-xs text-base-400">
								<div className="h-px w-10 bg-base-700"></div>
								<div>{t("styleUpdateCoverDialog.or")}</div>
								<div className="h-px w-10 bg-base-700"></div>
							</div>
							<Button
								trackingLabel="select_images"
								aria-label={t("styleUpdateCoverDialog.ariaSelectCoverImage")}
								size="sm"
								variant="primary"
								onClick={() => {
									void selectImages()
								}}
							>
								{t("styleUpdateCoverDialog.selectImages")}
							</Button>
						</div>
					)}
				</div>
			</div>
		</Dialog>
	)
}
